/**
 * src/lib/parentRecipient.ts
 * ------------------------------------------------------------------
 * WHICH ADDRESS DOES THE SCHOOL ACTUALLY WRITE TO?
 *
 * LKC sent 905 fee reminders and reached 57 families. The domain check in
 * reminderReach explained why the mail bounced — 577 pupils carried an
 * address at `parent.lkc.ac.bw`, a domain with no mail exchanger, whose
 * local part was the pupil's own surname. Nobody ever gave those addresses;
 * a data import manufactured them.
 *
 * What it did not explain is why the school kept sending to them AFTER the
 * parents had given real ones. That turned out to be a disagreement about
 * where a parent's address lives:
 *
 *   the sender  (functions/finance.js) read `parentContact.email` and, if
 *               that was missing, the family's primaryContact. It never
 *               looked at the guardians.
 *   the screen  (lib/admissions getPrimaryContact) read the guardians first
 *               and called `parentContact` "legacy".
 *
 * So an address the office could see on the profile was not the address the
 *07:00 sweep used, and three of the four paths that update a guardian's
 * email never wrote `parentContact` at all — including the live one, where
 * a parent changes their own details in the portal and an admin approves it.
 *
 * THE RULE THE SCHOOL GAVE, SEPT 2026: an address supplied after the app was
 * first loaded supersedes whatever the import put there, and the guardian who
 * has actually been in touch is the primary contact on record.
 *
 * That makes the parent's own PORTAL ACCOUNT the best evidence there is. It
 * postdates the import by definition; the parent chose it; credentials were
 * delivered to it; and 600 of the 733 have signed in with it, which no
 * guardian record can demonstrate. So it is consulted first, and the
 * tie-break between two accounts on one pupil is who signed in most
 * recently — the same question as who has been in touch.
 *
 * Nothing here reads Firestore. It is given the candidates and returns a
 * choice, so both the app and the Cloud Functions can be sure of the same
 * answer, and so the answer can be tested.
 *
 * MIRRORED at functions/lib/parentRecipient.js. Change one, change both.
 * ------------------------------------------------------------------ */

/**
 * Manufactured, never given by a parent.
 *
 * Not a guess about DNS — reminderReach does that properly, with a real MX
 * lookup, and should stay the general mechanism. This is a fact about where
 * these particular strings came from: `parent.lkc.ac.bw` was invented by the
 * import from each pupil's surname, and `nomail.com` is what the admissions
 * form writes when a parent gave nothing. Mailing either is not a long shot,
 * it is a certainty of failure.
 */
export const PLACEHOLDER_DOMAINS = ['parent.lkc.ac.bw', 'nomail.com', 'example.com'];

/** A staff address, whatever field it turns up in. Mailing the school and
 *  calling the family chased is worse than admitting we have no address. */
export const SCHOOL_DOMAINS = ['lkcportal.com'];

const SHAPE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function domainOf(email: unknown): string {
  return String(email ?? '').trim().toLowerCase().split('@').pop() ?? '';
}

/** Well formed, and not one of the addresses we know nobody reads. */
export function isRealAddress(email: unknown): email is string {
  const e = String(email ?? '').trim();
  if (!SHAPE.test(e)) return false;
  const d = domainOf(e);
  return !PLACEHOLDER_DOMAINS.includes(d) && !SCHOOL_DOMAINS.includes(d);
}

export interface GuardianLike {
  id?: string;
  fullName?: string;
  email?: string;
  isPrimaryContact?: boolean;
}

/** A `users` document with a parentAccount, as much of it as matters here. */
export interface ParentAccountLike {
  uid?: string;
  email?: string;
  displayName?: string;
  isActive?: boolean;
  /** ISO. The strongest evidence an address works: mail reached it, and
   *  somebody opened it and signed in. */
  lastLoginAt?: string;
  createdAt?: string;
  parentAccount?: { guardianId?: string; studentIds?: string[] };
}

export interface StudentContactLike {
  admissions?: { guardians?: GuardianLike[] };
  parentContact?: { email?: string; guardianName?: string };
}

export type RecipientSource = 'account' | 'guardian' | 'parentContact' | 'family';

export interface ResolvedRecipient {
  email: string;
  source: RecipientSource;
  /** Which guardian this address belongs to, when that is known. */
  guardianId?: string;
  name?: string;
  /** True when the holder has signed in — this address is proven, not assumed. */
  proven?: boolean;
}

/**
 * Rank the parent accounts linked to one pupil, best first.
 *
 * Most recent sign-in wins, because the school's rule is that the guardian
 * who has been in touch is the one on record, and a sign-in is the most
 * recent contact the system can actually see. An account nobody has used
 * falls back to how recently it was created; two accounts with neither are
 * ordered by address so the choice is stable rather than whatever order
 * Firestore happened to return.
 */
export function rankParentAccounts(accounts: ParentAccountLike[]): ParentAccountLike[] {
  return accounts
    .filter((a) => a.isActive !== false && isRealAddress(a.email))
    .slice()
    .sort((a, b) => (
      (b.lastLoginAt ?? '').localeCompare(a.lastLoginAt ?? '')
      || (b.createdAt ?? '').localeCompare(a.createdAt ?? '')
      || String(a.email).localeCompare(String(b.email))
    ));
}

/** The guardian record the school should be treating as primary, if the
 *  accounts point at one. Used by the backfill, not by sending. */
export function preferredGuardianId(
  student: StudentContactLike,
  accounts: ParentAccountLike[],
): string | null {
  const guardians = student.admissions?.guardians ?? [];
  for (const a of rankParentAccounts(accounts)) {
    const gid = a.parentAccount?.guardianId;
    if (gid && guardians.some((g) => g.id === gid)) return gid;
    // An account whose guardianId no longer matches any record still
    // identifies its guardian by the address it signs in with.
    const byEmail = guardians.find(
      (g) => String(g.email ?? '').toLowerCase() === String(a.email).toLowerCase(),
    );
    if (byEmail?.id) return byEmail.id;
  }
  return null;
}

/**
 * The address to write to for this pupil, and where it came from.
 *
 * Ordered by how recent and how proven the evidence is, which is the
 * school's rule rather than a preference of this module's:
 *
 *   1. a linked portal account   — chosen by the parent, after the import,
 *                                  and usually signed in with
 *   2. a guardian record         — primary first, then any other guardian,
 *                                  because an address given for the second
 *                                  parent still reaches the household
 *   3. parentContact.email       — the import's field, kept last of the
 *                                  per-pupil sources for exactly that reason
 *   4. the family contact        — a household-level fallback
 *
 * Returns null rather than a placeholder. "We have no address for this
 * family" is a fact the school can act on; an address that cannot receive
 * mail is one it cannot.
 */
export function resolveParentRecipient(args: {
  student: StudentContactLike;
  /** Active parent accounts linked to THIS pupil. */
  accounts?: ParentAccountLike[];
  /** The family's primaryContact.email, when the pupil belongs to one. */
  familyEmail?: string | null;
}): ResolvedRecipient | null {
  const { student, accounts = [], familyEmail } = args;

  const best = rankParentAccounts(accounts)[0];
  if (best) {
    return {
      email: String(best.email).trim(),
      source: 'account',
      guardianId: best.parentAccount?.guardianId,
      name: best.displayName,
      proven: !!best.lastLoginAt,
    };
  }

  const guardians = student.admissions?.guardians ?? [];
  const ordered = [
    ...guardians.filter((g) => g.isPrimaryContact),
    ...guardians.filter((g) => !g.isPrimaryContact),
  ];
  for (const g of ordered) {
    if (isRealAddress(g.email)) {
      return { email: String(g.email).trim(), source: 'guardian', guardianId: g.id, name: g.fullName };
    }
  }

  const pc = student.parentContact?.email;
  if (isRealAddress(pc)) {
    return { email: String(pc).trim(), source: 'parentContact', name: student.parentContact?.guardianName };
  }

  if (isRealAddress(familyEmail)) {
    return { email: String(familyEmail).trim(), source: 'family' };
  }

  return null;
}

/**
 * Index parent accounts by the pupils they are linked to.
 *
 * A household with three children produces one account against three pupil
 * ids, so the caller reads the whole `users` collection once and asks this
 * for each pupil rather than querying per pupil.
 */
export function accountsByStudent(
  users: ParentAccountLike[],
): Map<string, ParentAccountLike[]> {
  const out = new Map<string, ParentAccountLike[]>();
  for (const u of users) {
    const ids = u.parentAccount?.studentIds;
    if (!Array.isArray(ids)) continue;
    for (const id of ids) {
      const list = out.get(id);
      if (list) list.push(u); else out.set(id, [u]);
    }
  }
  return out;
}
